/**
 * Documents — Business Document Vault™ main route (`/documents`).
 *
 * Layout:
 *   - Page header (module kicker + H1 + subtitle)
 *   - Search bar (fuzzy display_name) + tag filter chips
 *   - Category tab strip (4 tabs: Business Foundation, Financial, Grant, General)
 *   - Upload widget scoped to the active tab
 *   - Card grid populated from `/api/documents?category=<tab>`
 *
 * Free-tier quota 402 (count or MB) surfaces an inline
 * `<PremiumUpgradePanel scope="documents" />` above the grid — same
 * pattern as G2A `/grants/matches` and G2B `/grants/pipeline`.
 *
 * Cross-category state is intentionally shared via a single
 * `/api/documents` fetch (no `?category=` filter) — the tab component
 * partitions the results client-side. Keeps the grid a single source
 * of truth and lets the empty-state hint at other tabs.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  FolderOpen,
  Loader2,
  X,
  AlertTriangle,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import DocumentsShell from "@/components/documents/DocumentsShell";
import UploadWidget from "@/components/documents/UploadWidget";
import DocumentCard from "@/components/documents/DocumentCard";
import DocumentEditModal from "@/components/documents/DocumentEditModal";
import PremiumUpgradePanel from "@/components/grants/PremiumUpgradePanel";
import { formatQuotaUsage } from "@/components/documents/utils";

const CATEGORIES = [
  {
    key: "business_foundation",
    label: "Business Foundation",
    emptyCopy:
      "Upload your EIN letter, Articles, Operating Agreement, and licenses here.",
  },
  {
    key: "financial",
    label: "Financial",
    emptyCopy:
      "Upload tax returns, P&L, balance sheets, and bank statements here.",
  },
  {
    key: "grant",
    label: "Grant",
    emptyCopy:
      "Upload your business plan, project budget, resumes, and capability statement here.",
  },
  {
    key: "general",
    label: "General",
    emptyCopy: "Any other supporting documents live here.",
  },
];

export default function Documents() {
  const [activeCategory, setActiveCategory] = useState("business_foundation");
  const [state, setState] = useState({
    loading: true,
    docs: [],
    error: null,
  });
  const [taxonomy, setTaxonomy] = useState(null);
  const [quota, setQuota] = useState(null);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState(null);
  const [editing, setEditing] = useState(null);
  const [banner, setBanner] = useState(null); // {type: 'error'|'upgrade', message?, cap?, quota_mb?}
  const [pendingDelete, setPendingDelete] = useState(null); // {doc, timerId}

  const fetchAll = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const [docsRes, quotaRes] = await Promise.all([
        api.get("/documents", { params: { limit: 200 } }),
        api.get("/documents/quota"),
      ]);
      setState({ loading: false, docs: docsRes.data.items || [], error: null });
      setQuota(quotaRes.data);
    } catch (err) {
      setState({
        loading: false,
        docs: [],
        error: formatApiError(err),
      });
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    api
      .get("/documents/taxonomy")
      .then((res) => setTaxonomy(res.data.categories || {}))
      .catch(() => setTaxonomy({}));
  }, []);

  // Cleanup any pending undo timer on unmount.
  useEffect(() => {
    return () => {
      if (pendingDelete?.timerId) clearTimeout(pendingDelete.timerId);
    };
  }, [pendingDelete]);

  const perCategoryCounts = useMemo(() => {
    const counts = { business_foundation: 0, financial: 0, grant: 0, general: 0 };
    for (const d of state.docs) {
      if (counts[d.category] != null) counts[d.category] += 1;
    }
    return counts;
  }, [state.docs]);

  const allTags = useMemo(() => {
    const set = new Set();
    for (const d of state.docs) {
      for (const t of d.tags || []) set.add(t);
    }
    return Array.from(set).sort();
  }, [state.docs]);

  const visibleDocs = useMemo(() => {
    let items = state.docs.filter((d) => d.category === activeCategory);
    if (search.trim()) {
      const needle = search.trim().toLowerCase();
      items = items.filter((d) =>
        (d.display_name || "").toLowerCase().includes(needle),
      );
    }
    if (tagFilter) {
      items = items.filter((d) => (d.tags || []).includes(tagFilter));
    }
    return items;
  }, [state.docs, activeCategory, search, tagFilter]);

  function handleUploadError(err) {
    if (typeof err === "object" && err?.code === "doc_count_cap_reached") {
      setBanner({
        type: "upgrade",
        variant: "count",
        cap: err.cap,
        tier: err.tier,
      });
    } else if (typeof err === "object" && err?.code === "doc_quota_reached") {
      setBanner({
        type: "upgrade",
        variant: "quota",
        quota_mb: err.quota_mb,
        tier: err.tier,
      });
    } else {
      setBanner({ type: "error", message: err });
    }
  }

  async function handleUploaded(newDoc) {
    setState((s) => ({ ...s, docs: [newDoc, ...s.docs] }));
    setBanner(null);
    // Refresh quota so the progress bar stays honest.
    try {
      const q = await api.get("/documents/quota");
      setQuota(q.data);
    } catch {}
  }

  async function handleRename(doc, newName) {
    try {
      const res = await api.patch(`/documents/${doc.id}`, { display_name: newName });
      setState((s) => ({
        ...s,
        docs: s.docs.map((d) => (d.id === doc.id ? res.data : d)),
      }));
    } catch (err) {
      setBanner({ type: "error", message: formatApiError(err) });
    }
  }

  async function handleDownload(doc) {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: doc.mime_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.display_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setBanner({ type: "error", message: formatApiError(err) });
    }
  }

  // Optimistic soft-delete with 30-second window that shows an
  // Undo affordance. If the user clicks Undo we call PATCH to
  // toggle is_active back — but the DELETE endpoint is
  // idempotent-safe and there's no PATCH-to-restore endpoint yet,
  // so within-session we just splice the row back in and skip
  // committing the delete until the timer expires.
  async function handleDelete(doc) {
    // First, mark the card visually as deleted in the local state.
    setState((s) => ({
      ...s,
      docs: s.docs.map((d) => (d.id === doc.id ? { ...d, __pendingDelete: true } : d)),
    }));

    const timerId = setTimeout(async () => {
      try {
        await api.delete(`/documents/${doc.id}`);
        setState((s) => ({
          ...s,
          docs: s.docs.filter((d) => d.id !== doc.id),
        }));
        setPendingDelete(null);
        // Refresh quota after committed delete.
        try {
          const q = await api.get("/documents/quota");
          setQuota(q.data);
        } catch {}
      } catch (err) {
        // Restore visually on server failure.
        setState((s) => ({
          ...s,
          docs: s.docs.map((d) =>
            d.id === doc.id ? { ...d, __pendingDelete: false } : d,
          ),
        }));
        setPendingDelete(null);
        setBanner({ type: "error", message: formatApiError(err) });
      }
    }, 30000);

    setPendingDelete({ doc, timerId });
  }

  function undoDelete() {
    if (!pendingDelete) return;
    clearTimeout(pendingDelete.timerId);
    setState((s) => ({
      ...s,
      docs: s.docs.map((d) =>
        d.id === pendingDelete.doc.id ? { ...d, __pendingDelete: false } : d,
      ),
    }));
    setPendingDelete(null);
  }

  const docTypesForActive = taxonomy?.[activeCategory] || [];
  const activeCategoryMeta = CATEGORIES.find((c) => c.key === activeCategory);

  return (
    <DocumentsShell testId="documents-root">
      <header className="mt-6">
        <p className="text-xs uppercase tracking-[0.28em] text-[var(--ds-color-ink-muted)]">
          Business Document Vault
        </p>
        <h1 className="mt-2 text-[36px] sm:text-[48px] font-semibold tracking-tight text-[var(--ds-color-ink)]">
          Documents
        </h1>
        <p className="mt-3 text-sm text-[var(--ds-color-ink-muted)] max-w-2xl">
          A single place for the paperwork your business relies on — foundation
          docs, financials, grant materials, and general support files.
          Uploads are educational storage only; sponsors and funders do not see
          these files unless you submit them.
        </p>
      </header>

      {quota && (
        <div
          data-testid="documents-quota-strip"
          className="mt-5 rounded-[var(--ds-radius-xl)] p-3 flex items-center gap-4 flex-wrap"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px solid var(--ds-color-border)",
          }}
        >
          <div className="flex-1 min-w-[220px]">
            <p className="text-[10px] uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
              {quota.tier === "premium" ? "Premium storage" : "Free storage"}
            </p>
            <div className="mt-1 flex items-center gap-2">
              <div
                className="flex-1 h-1.5 rounded-full overflow-hidden"
                style={{ background: "var(--ds-color-surface-inset)" }}
              >
                <div
                  data-testid="documents-quota-progress"
                  className="h-full transition-all"
                  style={{
                    width: `${Math.min(
                      100,
                      Math.round((quota.bytes_used / quota.quota_bytes) * 100),
                    )}%`,
                    background: "var(--ds-color-accent)",
                  }}
                />
              </div>
              <span className="text-[11px] text-[var(--ds-color-ink-muted)] whitespace-nowrap">
                {formatQuotaUsage(quota.bytes_used, quota.quota_bytes)}
              </span>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.14em] text-[var(--ds-color-ink-muted)]">
              Documents
            </p>
            <p
              data-testid="documents-quota-count"
              className="text-sm text-[var(--ds-color-ink)] font-semibold"
            >
              {quota.count_used} of {quota.count_cap}
            </p>
          </div>
        </div>
      )}

      {banner?.type === "upgrade" && (
        <PremiumUpgradePanel scope="documents" />
      )}

      {banner?.type === "error" && (
        <div
          data-testid="documents-error-banner"
          role="alert"
          className="mt-6 rounded-[var(--ds-radius-md)] p-4 flex items-start gap-3"
          style={{
            background: "var(--ds-color-danger-soft)",
            border: "1px solid var(--ds-color-danger)",
          }}
        >
          <AlertTriangle
            className="w-4 h-4 mt-0.5 flex-shrink-0"
            style={{ color: "var(--ds-color-danger)" }}
            aria-hidden
          />
          <p className="flex-1 text-[13px] leading-relaxed text-[var(--ds-color-ink)]">
            {typeof banner.message === "string"
              ? banner.message
              : "Something went wrong. Please try again."}
          </p>
          <button
            type="button"
            onClick={() => setBanner(null)}
            data-testid="documents-error-banner-dismiss"
            className="flex-shrink-0 text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
            aria-label="Dismiss error"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <label
          className="flex-1 min-w-[220px] inline-flex items-center gap-2 rounded-full px-4 py-2"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px solid var(--ds-color-border)",
          }}
        >
          <Search className="w-4 h-4 text-[var(--ds-color-ink-muted)] flex-shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documents by name"
            data-testid="documents-search-input"
            className="flex-1 bg-transparent text-sm text-[var(--ds-color-ink)] focus:outline-none"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              data-testid="documents-search-clear"
              className="text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)]"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </label>
        {allTags.length > 0 && (
          <div
            data-testid="documents-tag-filter-chips"
            className="flex items-center gap-1 flex-wrap"
          >
            {allTags.slice(0, 8).map((t) => {
              const active = tagFilter === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTagFilter(active ? null : t)}
                  data-testid={`documents-tag-chip-${t}`}
                  className="inline-flex items-center rounded-[var(--ds-radius-pill)] px-2.5 py-1 text-[11px] transition-colors"
                  style={{
                    background: active ? "var(--ds-color-accent-soft)" : "var(--ds-color-surface-muted)",
                    border: `1px solid ${active ? "var(--ds-color-accent)" : "var(--ds-color-border)"}`,
                    color: active ? "var(--ds-color-accent)" : "var(--ds-color-ink-muted)",
                    transitionDuration: "var(--ds-duration-fast)",
                  }}
                >
                  #{t}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <nav
        data-testid="documents-tab-strip"
        className="mt-6 flex items-center gap-1 overflow-x-auto"
      >
        {CATEGORIES.map((cat) => {
          const active = cat.key === activeCategory;
          return (
            <button
              key={cat.key}
              type="button"
              onClick={() => setActiveCategory(cat.key)}
              data-testid={`documents-tab-${cat.key}`}
              className="inline-flex items-center gap-2 rounded-[var(--ds-radius-pill)] px-4 py-2 text-xs font-semibold whitespace-nowrap transition-colors"
              style={{
                background: active ? "var(--ds-color-accent-soft)" : "transparent",
                border: `1px solid ${active ? "var(--ds-color-accent)" : "var(--ds-color-border)"}`,
                color: active ? "var(--ds-color-accent)" : "var(--ds-color-ink-muted)",
                transitionDuration: "var(--ds-duration-fast)",
              }}
            >
              {cat.label}
              <span
                className="inline-flex items-center justify-center rounded-[var(--ds-radius-pill)] h-5 min-w-[20px] px-1 text-[10px]"
                style={{
                  background: active ? "var(--ds-color-accent)" : "var(--ds-color-surface-muted)",
                  color: active ? "var(--ds-color-accent-ink)" : "var(--ds-color-ink-muted)",
                }}
              >
                {perCategoryCounts[cat.key]}
              </span>
            </button>
          );
        })}
      </nav>

      <div className="mt-6">
        <UploadWidget
          category={activeCategory}
          docTypes={docTypesForActive}
          onUploaded={handleUploaded}
          onError={handleUploadError}
        />
      </div>

      {state.loading && (
        <div
          data-testid="documents-loading"
          className="mt-10 flex items-center gap-2 text-sm text-[var(--ds-color-ink-muted)]"
        >
          <Loader2 className="w-4 h-4 animate-spin" /> Loading documents…
        </div>
      )}

      {!state.loading && !state.error && visibleDocs.length === 0 && (
        <div
          data-testid={`documents-empty-${activeCategory}`}
          className="mt-8 rounded-[var(--ds-radius-xl)] p-10 text-center"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px dashed var(--ds-color-border)",
          }}
        >
          <FolderOpen className="mx-auto w-6 h-6 text-[var(--ds-color-accent)]" />
          <p className="mt-3 text-[var(--ds-color-ink)] font-semibold">
            No {activeCategoryMeta?.label.toLowerCase()} documents yet.
          </p>
          <p className="mt-2 text-sm text-[var(--ds-color-ink-muted)]">
            {activeCategoryMeta?.emptyCopy}
          </p>
        </div>
      )}

      {!state.loading && !state.error && visibleDocs.length > 0 && (
        <div
          data-testid="documents-grid"
          className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {visibleDocs.map((doc) => (
            <DocumentCard
              key={doc.id}
              doc={doc}
              justDeleted={doc.__pendingDelete}
              onUndo={undoDelete}
              onRenamed={(newName) => handleRename(doc, newName)}
              onEdit={() => setEditing(doc)}
              onDownload={() => handleDownload(doc)}
              onDelete={() => handleDelete(doc)}
            />
          ))}
        </div>
      )}

      <DocumentEditModal
        doc={editing}
        open={!!editing}
        onClose={() => setEditing(null)}
        onSaved={(updated) => {
          setState((s) => ({
            ...s,
            docs: s.docs.map((d) => (d.id === updated.id ? updated : d)),
          }));
        }}
      />
    </DocumentsShell>
  );
}
